import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Spinner } from "@/shared/components/ui/spinner";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";
import { useResolvedById } from "@/shared/hooks/useResolvedById";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { rentalService } from "@/features/rentals/services/rental.service";
import type { RentalResponseDTO } from "@/features/rentals/types/rental.types";

interface RentalAsyncSelectProps {
  value: number | null;
  onChange: (rentalId: number | null) => void;
  disabled?: boolean;
  inputId?: string;
}

const DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 8;

function formatRentalLabel(rental: RentalResponseDTO): string {
  const parts: string[] = [`Aluguel #${rental.id}`];
  if (rental.contractNumber) parts.push(`Contrato ${rental.contractNumber}`);
  if (rental.sellerName) parts.push(`Vendedor: ${rental.sellerName}`);
  return parts.join(" · ");
}

export function RentalAsyncSelect({
  value,
  onChange,
  disabled = false,
  inputId,
}: RentalAsyncSelectProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<RentalResponseDTO[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchRentalById = useCallback(
    (id: number, signal: AbortSignal) => rentalService.getById(id, { signal }),
    [],
  );
  const [selected, preloadRental, resolveError] = useResolvedById(
    value,
    fetchRentalById,
  );

  useEffect(() => {
    if (resolveError) {
      toast.error(getApiErrorMessage(resolveError, "Erro ao carregar aluguel"));
    }
  }, [resolveError]);

  const fetchRentals = useCallback(
    async (term: string, signal: AbortSignal) => {
      const response = await rentalService.adminSearch(
        { contractNumbers: [term.trim()], size: SEARCH_PAGE_SIZE },
        { signal },
      );
      return response.content;
    },
    [],
  );

  useDebouncedSearch({
    term: searchTerm,
    fetcher: fetchRentals,
    setItems: setResults,
    setLoading: setSearching,
    onError: (e) =>
      toast.error(getApiErrorMessage(e, "Erro ao buscar aluguéis")),
    debounceMs: DEBOUNCE_MS,
  });

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleSelect = useCallback(
    (rental: RentalResponseDTO) => {
      preloadRental(rental.id, rental);
      onChange(rental.id);
      setSearchTerm("");
      setResults([]);
      setOpen(false);
    },
    [onChange, preloadRental],
  );

  const handleClear = useCallback(() => {
    onChange(null);
  }, [onChange]);

  if (selected) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm">
        <span className="flex-1">{formatRentalLabel(selected)}</span>
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled}
          aria-label="Trocar aluguel"
          className="rounded-full p-0.5 hover:bg-accent"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          id={inputId}
          value={searchTerm}
          placeholder="Buscar pelo número do contrato…"
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          disabled={disabled}
          className="pl-9"
        />
      </div>

      {open && searchTerm.trim().length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-y-auto z-50">
          {searching ? (
            <div className="flex items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
              <Spinner className="w-4 h-4" />
              Buscando…
            </div>
          ) : results.length === 0 ? (
            <div className="p-3 text-center text-sm text-muted-foreground">
              Nenhum aluguel encontrado
            </div>
          ) : (
            results.map((rental) => (
              <button
                key={rental.id}
                type="button"
                onClick={() => handleSelect(rental)}
                className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
              >
                {formatRentalLabel(rental)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
